import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { Notification } from '../NotificationLoginComponent';
import { BottomNav } from '../BottomNav';
import { GetAllAccounts, MakeUserPermanent, RemoveAccount, UpdateUserRank } from '../../Backend/ApiCalls';
import { getAgeTypeLabel, normalizeAgeType } from '../../utils/ageType';
import './AdministracionUsuariosComponent.css';

const AGE_FILTERS = [
  { key: 'ALL', label: 'Todos' },
  { key: 'EXENTO', label: 'Exentos' },
  { key: 'INFANTIL', label: 'Infantiles' },
  { key: 'JUVENIL', label: 'Juveniles' },
  { key: 'ADULTO', label: 'Adultos' },
  { key: 'TEMPORAL', label: 'Temporales' },
];

const SORT_OPTIONS = [
  { key: 'name', label: 'Apellido' },
  { key: 'dni', label: 'DNI' },
  { key: 'telephone', label: 'Teléfono' },
  { key: 'email', label: 'Email' },
  { key: 'birthday', label: 'Fecha de nacimiento' },
  { key: 'rank', label: 'Rango' },
  { key: 'ageType', label: 'Edad' },
];

export const AdministracionUsuariosComponent = (props) => {
  const { user } = props;
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [ageFilter, setAgeFilter] = useState('ALL');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const [isErrorNoti, setIsErrorNoti] = useState(false);

  const usersPerPage = 40;

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await GetAllAccounts();
        setUsers(
          Array.isArray(data)
            ? data.map((account) => ({ ...account, ageType: normalizeAgeType(account.ageType) }))
            : []
        );
        setCurrentPage(1);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
    window.addEventListener('focus', fetchUsers);
    return () => window.removeEventListener('focus', fetchUsers);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight || document.documentElement.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - 16) {
        setCurrentPage((page) => page + 1);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fullNameOf = (account) => `${account.name || ''} ${account.surname || ''}`.trim();

  const firstSurnameOf = (account) => (account.surname || '').split(' ')[0].toLowerCase();

  const safeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return value.trim()
          ? value.split(',').map((item) => item.trim()).filter(Boolean)
          : [];
      }
    }
    return [];
  };

  const partnersText = (account) => safeArray(account.partners).join(', ');
  const rewardsText = (account) => safeArray(account.rewards).join(', ');
  // Diccionario centralizado de roles del sistema:
  //   0 = Usuario raso
  //   2 = Vicepresidente (acceso solo a Gestión de encuestas)
  //   3 = Administrador (acceso completo)
  const RANK_OPTIONS = [
    { value: 0, label: 'Usuario' },
    { value: 2, label: 'Vicepresidente' },
    { value: 3, label: 'Administrador' },
  ];

  const rankText = (account) => {
    const r = Number(account?.userRank);
    if (r === 3) return 'Administrador';
    if (r === 2) return 'Vicepresidente';
    return 'Usuario';
  };
  const ageLabel = (account) => getAgeTypeLabel(account.ageType);
  const isTemporaryUser = (account) => normalizeAgeType(account.ageType) === 4;

  const ageFilterMatches = (account) => {
    if (ageFilter === 'ALL') return true;
    return ageLabel(account).toUpperCase() === ageFilter;
  };

  const getValueBySortKey = (account, key) => {
    switch (key) {
      case 'name':
        return firstSurnameOf(account);
      case 'dni':
        return (account.dni || '').toLowerCase();
      case 'telephone':
        return (account.telephone || '').toLowerCase();
      case 'email':
        return (account.email || '').toLowerCase();
      case 'birthday':
        return account.birthday ? new Date(account.birthday).getTime() : 0;
      case 'rank':
        return rankText(account).toLowerCase();
      case 'ageType':
        return normalizeAgeType(account.ageType);
      default:
        return '';
    }
  };

  const stats = useMemo(() => {
    return users.reduce((acc, account) => {
      const label = ageLabel(account);
      acc.total += 1;
      acc[label] += 1;
      return acc;
    }, {
      total: 0,
      Exento: 0,
      Infantil: 0,
      Juvenil: 0,
      Adulto: 0,
      Temporal: 0,
    });
  }, [users]);

  const filteredUsers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const filtered = users.filter((account) => {
      if (!ageFilterMatches(account)) return false;
      if (!query) return true;

      const haystack = [
        fullNameOf(account),
        account.dni || '',
        account.email || '',
        account.telephone || '',
        partnersText(account),
        rewardsText(account),
        ageLabel(account),
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });

    const direction = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const valueA = getValueBySortKey(a, sortKey);
      const valueB = getValueBySortKey(b, sortKey);

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return (valueA - valueB) * direction;
      }

      return String(valueA).localeCompare(String(valueB), 'es', { sensitivity: 'base' }) * direction;
    });
  }, [users, searchTerm, ageFilter, sortKey, sortDir]);

  const pagedUsers = useMemo(() => {
    return filteredUsers.slice(0, currentPage * usersPerPage);
  }, [filteredUsers, currentPage]);

  const showFeedback = (message, isError = false) => {
    setNotificationMessage(message);
    setIsErrorNoti(isError);
    setShowNotification(true);
    window.setTimeout(() => setShowNotification(false), 5000);
  };

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  // Cambia el rol del usuario al valor seleccionado en el <select>.
  const handleRankChange = async (userId, rawValue) => {
    const newRank = Number(rawValue);
    if (![0, 2, 3].includes(newRank)) {
      showFeedback('Rol no válido', true);
      return;
    }

    const account = users.find((item) => item.Id === userId);
    if (!account) return;
    if (Number(account.userRank) === newRank) return; // no-op si no cambia

    const { response } = await UpdateUserRank(userId, newRank);

    if (response?.ok) {
      setUsers((prev) => prev.map((item) => (
        item.Id === userId ? { ...item, userRank: newRank } : item
      )));
      const labelMap = { 0: 'Usuario', 2: 'Vicepresidente', 3: 'Administrador' };
      showFeedback(`Rol actualizado: ${labelMap[newRank]}`);
      return;
    }

    showFeedback('Error al actualizar el rol del usuario', true);
  };

  const handleEditUser = (userId) => navigate(`/editar-usuario/${userId}`);

  const handleMakePermanent = (userId) => {
    const account = users.find((item) => item.Id === userId);
    if (!account) return;

    confirmAlert({
      title: 'Convertir en permanente',
      message: `Se calculará la edad real de ${fullNameOf(account)} a partir de su fecha de nacimiento. ¿Quieres continuar?`,
      buttons: [
        {
          label: 'Sí',
          onClick: async () => {
            const { response } = await MakeUserPermanent(userId);

            if (response?.ok) {
              const { data } = await GetAllAccounts();
              setUsers(
                Array.isArray(data)
                  ? data.map((item) => ({ ...item, ageType: normalizeAgeType(item.ageType) }))
                  : []
              );
              setCurrentPage(1);
              showFeedback('Usuario convertido en permanente');
              return;
            }

            showFeedback('Error al convertir el usuario en permanente', true);
          },
        },
        {
          label: 'No',
          onClick: () => showFeedback('Operación cancelada', true),
        },
      ],
    });
  };

  const handleDeleteUser = (userId) => {
    const account = users.find((item) => item.Id === userId);

    confirmAlert({
      title: 'Confirmación de eliminación',
      message: `¿Estás seguro que quieres eliminar a ${fullNameOf(account)}?`,
      buttons: [
        {
          label: 'Sí',
          onClick: async () => {
            const { response } = await RemoveAccount(userId);

            if (response?.ok) {
              setUsers((prev) => prev.filter((item) => item.Id !== userId));
              setCurrentPage(1);
              showFeedback('Usuario eliminado con éxito');
              return;
            }

            showFeedback('Error al eliminar el usuario', true);
          },
        },
        {
          label: 'No',
          onClick: () => showFeedback('Operación cancelada', true),
        },
      ],
    });
  };

  const sortIndicator = (key) => {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <div className="administracion-usuarios">
      {showNotification && (
        <Notification message={notificationMessage} type={isErrorNoti ? 'error' : 'success'} />
      )}

      <BottomNav I18n={props.I18n} user={user} />

      <div className="recipient admin-users-shell">
        <div className="admin-users-header">
          <div>
            <h1 className="titulo-page">{props.I18n.UserList}</h1>
            <p className="admin-users-subtitle">
              Gestiona usuarios con una vista más cómoda para móvil, filtros rápidos y resumen por edades.
            </p>
          </div>

          <Link to="/crear-usuario" className="button-default button-create-user">
            {props.I18n.CreateUser}
          </Link>
        </div>

        <div className="poll-stats user-stats-panel">
          <div className="stat-pill">
            <strong>Total usuarios:</strong> {stats.total}
          </div>
          <div className="stat-row">
            <div className="stat-chip">Exentos: {stats.Exento}</div>
            <div className="stat-chip">Infantiles: {stats.Infantil}</div>
            <div className="stat-chip">Juveniles: {stats.Juvenil}</div>
            <div className="stat-chip">Adultos: {stats.Adulto}</div>
            <div className="stat-chip">Temporales: {stats.Temporal}</div>
          </div>
        </div>

        <section className="admin-users-controls">
          <div className="search-input-container admin-users-search">
            <input
              type="text"
              placeholder="Buscar por nombre, DNI, email, teléfono..."
              className="search-input"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setCurrentPage(1);
              }}
            />
            <FaSearch className="search-input-icon" />
          </div>

          <div className="admin-users-toolbar">
            <div className="admin-users-filter-group" aria-label="Filtrar por edad">
              {AGE_FILTERS.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  className={`filter-chip ${ageFilter === filter.key ? 'active' : ''}`}
                  onClick={() => {
                    setAgeFilter(filter.key);
                    setCurrentPage(1);
                  }}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            <div className="admin-users-sort-row">
              <label className="sort-control">
                <span>Ordenar por</span>
                <select
                  value={sortKey}
                  onChange={(event) => {
                    setSortKey(event.target.value);
                    setCurrentPage(1);
                  }}
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.key} value={option.key}>{option.label}</option>
                  ))}
                </select>
              </label>

              <button
                type="button"
                className="filter-chip sort-direction-chip active"
                onClick={() => {
                  setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
                  setCurrentPage(1);
                }}
              >
                {sortDir === 'asc' ? 'Ascendente' : 'Descendente'}
              </button>
            </div>
          </div>

          <p className="admin-users-results">
            Mostrando {pagedUsers.length} de {filteredUsers.length} usuarios filtrados.
          </p>
        </section>

        <div className="user-cards-list">
          {pagedUsers.map((account) => (
            <article key={account.Id} className="user-card">
              <div className="user-card-top">
                <div>
                  <h2>{fullNameOf(account)}</h2>
                  <p>{account.dni || 'Sin DNI'}</p>
                </div>
                <div className="user-card-badges">
                  <span className="user-badge age">{ageLabel(account)}</span>
                  <span className="user-badge rank">{rankText(account)}</span>
                </div>
              </div>

              <div className="user-card-grid">
                <div className="user-card-field">
                  <span>Email</span>
                  <strong>{account.email || '-'}</strong>
                </div>
                <div className="user-card-field">
                  <span>Teléfono</span>
                  <strong>{account.telephone || '-'}</strong>
                </div>
                <div className="user-card-field">
                  <span>Fecha de nacimiento</span>
                  <strong>{account.birthday ? new Date(account.birthday).toLocaleDateString('es-ES') : '-'}</strong>
                </div>
                <div className="user-card-field">
                  <span>Familiares</span>
                  <strong>{partnersText(account) || '-'}</strong>
                </div>
                <div className="user-card-field full">
                  <span>Recompensas</span>
                  <strong>{rewardsText(account) || '-'}</strong>
                </div>
              </div>

              <div className="user-card-actions">
                {!isTemporaryUser(account) && (
                  <>
                    <label className="rank-select-wrap">
                      <span className="rank-select-label">Rol</span>
                      <select
                        className="rank-select"
                        value={Number(account.userRank ?? 0)}
                        onChange={(e) => handleRankChange(account.Id, e.target.value)}
                        aria-label="Cambiar rol del usuario"
                      >
                        {RANK_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </label>
                    <button className="action-buttons" onClick={() => handleEditUser(account.Id)}>
                      Editar
                    </button>
                  </>
                )}
                {isTemporaryUser(account) && (
                  <button className="action-buttons permanent" onClick={() => handleMakePermanent(account.Id)}>
                    Hacer permanente
                  </button>
                )}
                <button className="action-buttons delete" onClick={() => handleDeleteUser(account.Id)}>
                  Eliminar
                </button>
              </div>
            </article>
          ))}
        </div>

        <div className="desktop-table-wrapper">
          <table className="user-table">
            <thead>
              <tr>
                <th><button className="th-btn" onClick={() => toggleSort('name')}>{props.I18n.Name}{sortIndicator('name')}</button></th>
                <th><button className="th-btn" onClick={() => toggleSort('dni')}>{props.I18n.DNI}{sortIndicator('dni')}</button></th>
                <th>{props.I18n.Partners}</th>
                <th><button className="th-btn" onClick={() => toggleSort('telephone')}>{props.I18n.Phone}{sortIndicator('telephone')}</button></th>
                <th><button className="th-btn" onClick={() => toggleSort('email')}>{props.I18n.Email}{sortIndicator('email')}</button></th>
                <th>{props.I18n.Rewards}</th>
                <th><button className="th-btn" onClick={() => toggleSort('birthday')}>{props.I18n.Birthdate}{sortIndicator('birthday')}</button></th>
                <th><button className="th-btn" onClick={() => toggleSort('rank')}>{props.I18n.Range}{sortIndicator('rank')}</button></th>
                <th><button className="th-btn" onClick={() => toggleSort('ageType')}>Edad{sortIndicator('ageType')}</button></th>
                <th>{props.I18n.Actions}</th>
              </tr>
            </thead>
            <tbody>
              {pagedUsers.map((account) => (
                <tr key={account.Id}>
                  <td>{fullNameOf(account)}</td>
                  <td>{account.dni}</td>
                  <td>{partnersText(account)}</td>
                  <td>{account.telephone}</td>
                  <td>{account.email}</td>
                  <td>{rewardsText(account)}</td>
                  <td>{account.birthday ? new Date(account.birthday).toLocaleDateString('es-ES') : ''}</td>
                  <td>{rankText(account)}</td>
                  <td>{ageLabel(account)}</td>
                  <td className="actions-column">
                    {!isTemporaryUser(account) && (
                      <>
                        <select
                          className="rank-select rank-select-inline"
                          value={Number(account.userRank ?? 0)}
                          onChange={(e) => handleRankChange(account.Id, e.target.value)}
                          aria-label="Cambiar rol del usuario"
                        >
                          {RANK_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        <button className="action-buttons" onClick={() => handleEditUser(account.Id)}>
                          Editar
                        </button>
                      </>
                    )}
                    {isTemporaryUser(account) && (
                      <button className="action-buttons permanent" onClick={() => handleMakePermanent(account.Id)}>
                        Hacer permanente
                      </button>
                    )}
                    <button className="action-buttons delete" onClick={() => handleDeleteUser(account.Id)}>
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
              {pagedUsers.length === 0 && (
                <tr>
                  <td colSpan={10} className="empty-row">Sin resultados</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
